import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, CTABand } from "@/components/site/Blocks";
import { localizedPath } from "@/lib/i18n/routes";
import { useI18n } from "@/lib/i18n/useLocale";
import { products } from "@/data/products";
import { buildHead } from "@/lib/seo-head";

export const Route = createFileRoute("/es/camino-del-inversionista")({
  head: () =>
    buildHead({
      title: "El Camino del Inversionista Inmobiliario | Simply Approved",
      description:
        "Desde su primera propiedad de inversión hasta un portafolio escalable. Encuentre el préstamo correcto para cada etapa del camino del inversionista.",
      url: "/es/camino-del-inversionista",
      locale: "es",
      keywords: "camino inversionista inmobiliario, primer préstamo propiedad de alquiler, escalar portafolio inversión, préstamos para cada etapa inversionista",
      breadcrumbs: [
        { name: "Inicio", url: "/es" },
        { name: "Camino del inversionista", url: "/es/camino-del-inversionista" },
      ],
      service: { name: "Préstamos para cada etapa del inversionista inmobiliario" },
    }),
  component: Journey,
});

const journeys = [
  { stage: "Primeras propiedades", need: "DSCR o FHA para inversión (no ocupante-propietario donde permitido)", solution: "Préstamos DSCR con calificación por renta y baja documentación personal." },
  { stage: "Escala con cash-out", need: "Liberar capital de propiedades existentes", solution: "Cash-out refinance DSCR hasta 80% LTV en propiedades elegibles." },
  { stage: "Mejoría y reinversión", need: "Renovar y vender o alquilar", solution: "Préstamos Fix & Flip y Bridge de corto plazo." },
  { stage: "Construcción de nuevas unidades", need: "Financiar construcción de cero", solution: "Préstamos de construcción con conversiones a permanente." },
  { stage: "Portafolio diversificado", need: "Propiedades en múltiples estados y plataformas", solution: "Préstamos STR, Section 8, multifamiliar y portafolio." },
];

function Journey() {
  const { locale } = useI18n();
  return (
    <SiteShell>
      <PageHero eyebrow="Camino del inversionista" title="El préstamo correcto para cada etapa." subtitle="De la primera propiedad de alquiler al portafolio diversificado, tenemos una solución para cada paso." />
      <Section>
        <SectionHeader title="Etapas del camino" center />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {journeys.map((j) => (
            <div key={j.stage} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold text-foreground">{j.stage}</h3>
              <p className="mt-2 text-sm font-semibold text-brand">{j.need}</p>
              <p className="mt-2 text-sm text-muted-foreground">{j.solution}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <SectionHeader title="Nuestros programas" center />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <Link
              key={p.slug}
              to={localizedPath(`/${p.slug}`, locale)}
              className="group rounded-2xl border border-border bg-card p-6 shadow-sm transition hover:border-brand hover:shadow-md"
            >
              <h3 className="font-display text-lg font-bold text-foreground group-hover:text-brand">{p.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
            </Link>
          ))}
        </div>
      </Section>
      <CTABand title="Encuentre su siguiente paso" body="Hable con un oficial de préstamos para mapear el mejor programa para su etapa." />
    </SiteShell>
  );
}
